import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../services/API'
import { formatNumber, tierLabel } from '../kit/helpers'
import {
  ALLOWANCE_METRICS,
  buildContractPayload,
  deriveContractFromTenant,
  legacyAgreedLimits,
  limitProblem,
  planAllowanceForTier,
  storedContractForm,
} from './tenantHelpers'

const Section = ({ title, children }) => (
  <Grid item xs={12}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        mb: 1,
        mt: 1,
      }}
    >
      {title}
    </Typography>
    <Grid container spacing={1.5}>
      {children}
    </Grid>
  </Grid>
)

const Field = ({ xs = 12, md = 4, ...props }) => (
  <Grid item xs={xs} md={md}>
    <TextField size="small" fullWidth {...props} />
  </Grid>
)

const DateField = (props) => <Field type="date" InputLabelProps={{ shrink: true }} {...props} />

const Hint = ({ children }) => (
  <Grid item xs={12}>
    <Typography variant="caption" color="text.secondary" display="block">
      {children}
    </Typography>
  </Grid>
)

// One limit per allowance metric. The label is always shrunk so the
// placeholder (the plan default, or "None") shows while the field is blank.
// `problems` holds the message for each limit that cannot be stored. Plain
// text inputs on purpose: a number input reports "1,500" or "abc" as blank,
// and a blank limit means "remove it".
const LimitFields = ({ values, placeholder, problems = {}, onChange }) =>
  ALLOWANCE_METRICS.map((metric) => (
    <Field
      key={metric.key}
      label={metric.label}
      value={values?.[metric.key] ?? ''}
      placeholder={placeholder(metric)}
      InputLabelProps={{ shrink: true }}
      inputProps={{ inputMode: metric.key === 'controlRoomHoursPerMonth' ? 'decimal' : 'numeric' }}
      error={!!problems[metric.key]}
      helperText={problems[metric.key] || undefined}
      onChange={(e) => onChange(metric.key, e.target.value)}
    />
  ))

// Edits the tenant's contract record (package, fees, included and additional
// limits, Stripe refs, quote and dates). Saves via PUT
// /admincrm/tenants/:id/contract, sending ONLY the fields edited in this
// session: a value that was merely loaded (or a default the form displays) is
// never written back, so it cannot undo a change made elsewhere meanwhile.
const ContractDialog = ({ open, tenant, onClose, onSaved }) => {
  const [form, setForm] = useState(() => deriveContractFromTenant(tenant))
  // Paths the user has edited since the dialog opened, e.g. 'monthlyFee' or
  // 'includedLimits.employees'.
  const [dirty, setDirty] = useState([])
  // The contract as stored when the dialog opened (no tier defaults).
  const [stored, setStored] = useState(() => storedContractForm(tenant))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Reset only when the dialog opens: a refreshed tenant arriving while it is
  // open must not discard what is being typed.
  useEffect(() => {
    if (open) {
      setForm(deriveContractFromTenant(tenant))
      setStored(storedContractForm(tenant))
      setDirty([])
      setError('')
    }
  }, [open])

  const touch = (path) => setDirty((prev) => (prev.includes(path) ? prev : [...prev, path]))
  const set = (field, value) => {
    touch(field)
    setForm((prev) => ({ ...prev, [field]: value }))
  }
  const setNested = (group, field, value) => {
    touch(`${group}.${field}`)
    setForm((prev) => ({ ...prev, [group]: { ...(prev[group] ?? {}), [field]: value } }))
  }

  // Only limits edited in this session are checked (and sent).
  const limitProblems = (group) =>
    Object.fromEntries(
      ALLOWANCE_METRICS.filter((metric) => dirty.includes(`${group}.${metric.key}`)).map((metric) => [
        metric.key,
        limitProblem(metric.key, form[group]?.[metric.key]),
      ]),
    )
  const includedProblems = limitProblems('includedLimits')
  const additionalProblems = limitProblems('additionalLimits')
  const invalidLimits = [...Object.values(includedProblems), ...Object.values(additionalProblems)].some(Boolean)

  const save = async () => {
    if (invalidLimits) {
      setError('Correct the highlighted limits before saving.')
      return
    }
    // Edited fields whose value differs from what is stored. Nothing changed:
    // nothing to send.
    const body = buildContractPayload(form, dirty, stored)
    if (Object.keys(body).length === 0) {
      onClose()
      return
    }
    setSaving(true)
    setError('')
    const res = await API.put(`/admincrm/tenants/${tenant._id}/contract`, body)
    setSaving(false)
    if (res.status !== 200) {
      setError(res.data?.message || res.data?.error || 'Could not save the contract.')
      return
    }
    onSaved(res.data)
  }

  const planName = tierLabel(tenant.subscriptionTier, '')
  const legacyLimits = legacyAgreedLimits(tenant)
  const includedPlaceholder = (metric) => {
    const planDefault = planAllowanceForTier(tenant.subscriptionTier, metric.key)
    if (planDefault !== null) return `Plan default: ${formatNumber(planDefault)}`
    return planName ? 'Not included in plan' : 'No plan default'
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Contract details</DialogTitle>
      <DialogContent>
        <Stack gap={1} paddingTop={0.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <Grid container spacing={1.5}>
            <Section title="Package & billing">
              <Field md={6} label="Package name" value={form.packageName} onChange={(e) => set('packageName', e.target.value)} />
              <Field md={3} label="Subscription tier" value={form.subscriptionTier} onChange={(e) => set('subscriptionTier', e.target.value)} />
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Billing cadence</InputLabel>
                  <Select
                    value={form.billingCadence}
                    label="Billing cadence"
                    onChange={(e) => set('billingCadence', e.target.value)}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="annually">Annually</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Field type="number" label="Monthly fee" value={form.monthlyFee} onChange={(e) => set('monthlyFee', e.target.value)} />
              <Field type="number" label="Annual fee" value={form.annualFee} onChange={(e) => set('annualFee', e.target.value)} />
              <Field label="Currency" value={form.currency} onChange={(e) => set('currency', e.target.value)} />
              {tenant.inInitialTrialPeriod && (
                <DateField label="Trial end date (legacy)" value={form.trialEnd} onChange={(e) => set('trialEnd', e.target.value)} />
              )}
            </Section>

            <Section title="Included limits">
              <Hint>
                What the contract includes. Leave a limit blank to use the
                {planName ? ` ${planName} ` : ' '}plan default (shown in the field) - only enter a
                figure where this customer&apos;s contract includes something different.
              </Hint>
              <LimitFields
                values={form.includedLimits}
                placeholder={includedPlaceholder}
                problems={includedProblems}
                onChange={(metric, value) => setNested('includedLimits', metric, value)}
              />
            </Section>

            <Section title="Additional limits">
              <Hint>
                Capacity agreed on top of what is included (for example an Enterprise+ package).
                The effective allowance is included plus additional, plus any complimentary
                capacity and, for employees, purchased seats. Blank means none.
              </Hint>
              <LimitFields
                values={form.additionalLimits}
                placeholder={() => 'None'}
                problems={additionalProblems}
                onChange={(metric, value) => setNested('additionalLimits', metric, value)}
              />
            </Section>

            {legacyLimits.length > 0 && (
              <Section title="Legacy agreed limits (read-only)">
                <Hint>
                  Recorded before included limits existed. They remain a historic override until
                  included limits are set, and can no longer be edited here.
                </Hint>
                {legacyLimits.map((limit) => (
                  <Field
                    key={limit.key}
                    label={limit.label}
                    value={formatNumber(limit.value)}
                    disabled
                    InputLabelProps={{ shrink: true }}
                  />
                ))}
              </Section>
            )}

            <Section title="Usage review">
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.usageReview.enabled}
                      onChange={(e) => setNested('usageReview', 'enabled', e.target.checked)}
                    />
                  }
                  label="Usage review enabled"
                />
              </Grid>
              <Field md={8} multiline minRows={2} label="Usage review notes" value={form.usageReview.notes} onChange={(e) => setNested('usageReview', 'notes', e.target.value)} />
              <Hint>
                Switching the review off stops months being assessed against the PTS and
                control-room allowances. The agreement acceptance, complimentary capacity and the
                review counters are managed from the Commercial usage tab, not from this form.
              </Hint>
            </Section>

            <Section title="Dates & ownership">
              <DateField label="Contract start" value={form.contractStartDate} onChange={(e) => set('contractStartDate', e.target.value)} />
              <DateField label="Renewal date" value={form.renewalDate} onChange={(e) => set('renewalDate', e.target.value)} />
              <Field label="Account manager" value={form.accountManager} onChange={(e) => set('accountManager', e.target.value)} />
            </Section>

            <Section title="Quote">
              <Field label="Quote reference" value={form.quote.quoteReference} onChange={(e) => setNested('quote', 'quoteReference', e.target.value)} />
              <DateField label="Quoted at" value={form.quote.quotedAt} onChange={(e) => setNested('quote', 'quotedAt', e.target.value)} />
              <DateField label="Accepted at" value={form.quote.acceptedAt} onChange={(e) => setNested('quote', 'acceptedAt', e.target.value)} />
              <Field label="Accepted by" value={form.quote.acceptedBy} onChange={(e) => setNested('quote', 'acceptedBy', e.target.value)} />
              <Field md={8} multiline minRows={2} label="Quote notes" value={form.quote.notes} onChange={(e) => setNested('quote', 'notes', e.target.value)} />
            </Section>

            <Section title="Stripe references">
              <Field md={6} label="Customer ID" value={form.stripe.customerId} onChange={(e) => setNested('stripe', 'customerId', e.target.value)} />
              <Field md={6} label="Subscription ID" value={form.stripe.subscriptionId} onChange={(e) => setNested('stripe', 'subscriptionId', e.target.value)} />
              <Field md={3} label="Price ID" value={form.stripe.priceId} onChange={(e) => setNested('stripe', 'priceId', e.target.value)} />
              <Field md={3} label="Product ID" value={form.stripe.productId} onChange={(e) => setNested('stripe', 'productId', e.target.value)} />
              <Field md={3} label="Latest invoice ID" value={form.stripe.latestInvoiceId} onChange={(e) => setNested('stripe', 'latestInvoiceId', e.target.value)} />
              <Field md={3} label="Status" value={form.stripe.status} onChange={(e) => setNested('stripe', 'status', e.target.value)} />
            </Section>

            <Section title="Notes">
              <Field md={12} multiline minRows={3} label="Contract notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </Section>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save contract'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ContractDialog
